import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse/main';
import { sanitizeNode } from '@atlaskit/editor-json-transformer/sanitize/sanitize-node';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';

import {
	REMIX_EXPANDS_EXPERIMENT,
	isRemixRestrictedNodeTypeWithExpands,
} from '@atlassian/malleable-ui-components/remix/remix-config';

import { isExperimentEnabled } from '@confluence/feature-experiments';

/**
 * Confluence product-side read of `REMIX_EXPANDS_EXPERIMENT`.
 *
 * Only invoked for expand node types (see `isRemixRestrictedNodeTypeWithExpands`), so filtering a
 * fragment without expands does not touch the experiment.
 */
const areRemixExpandsEnabled = (): boolean => isExperimentEnabled(REMIX_EXPANDS_EXPERIMENT);

/**
 * Filters an ADF fragment (array of JSONNode) by recursively removing nodes
 * whose type is in the Remix deny list at every level of the tree.
 * Returns null when given null.
 *
 * Uses the adf-utils `traverse` helper which drops nodes when a visitor
 * returns `false` and automatically rebuilds content arrays.
 *
 * Expands are the one deny-list exception: under `REMIX_EXPANDS_EXPERIMENT` the expand wrapper
 * (title attr included) and its inner content are preserved so "remix this expand" keeps its
 * meaning. Everything nested inside is still filtered normally.
 */
export const filterRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (nodes === null) {
		return null;
	}

	const result: JSONNode[] = [];
	for (const node of nodes) {
		const filtered = traverse(node as ADFEntity, {
			any: (adfNode) => {
				if (isRemixRestrictedNodeTypeWithExpands(adfNode.type, areRemixExpandsEnabled)) {
					return false;
				}
			},
		});
		if (filtered !== false) {
			result.push(filtered as JSONNode);
		}
	}
	return result;
};

/**
 * Converts editor selection JSON to canonical ADF before applying Remix's existing node filter.
 * The control path deliberately preserves the previous filter-only behavior.
 */
export const prepareRemixFragmentNodes = (nodes: JSONNode[] | null): JSONNode[] | null => {
	if (isExperimentEnabled('cc_maui_polish_changes_batch_6')) {
		if (nodes === null) {
			return null;
		}

		const sanitizedNodes = nodes.flatMap((node) => {
			// The sanitizer can remove an invalid root node at runtime even though its legacy return
			// type only exposes JSONNode.
			const sanitizedNode = sanitizeNode(node, { keepNestedTables: true }) as JSONNode | false;
			return sanitizedNode === false ? [] : [sanitizedNode];
		});

		return filterRemixFragmentNodes(sanitizedNodes);
	}

	return filterRemixFragmentNodes(nodes);
};
